/* ===============================
 * Rodal v1.8.2 https://chenjiahan.github.com/rodal
 * =============================== */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Draggable from 'react-draggable';

// env
const IN_BROWSER = typeof window !== 'undefined';
const UA = IN_BROWSER && window.navigator.userAgent.toLowerCase();
const IS_IE_9 = UA && UA.indexOf('msie 9.0') > 0;

const Dialog = props => {
  const animation =
    (props.animationType === 'enter'
      ? props.enterAnimation
      : props.leaveAnimation) || props.animation;

  const className = `${props.isDrag ? 'rodal-dialog-drag' :'rodal-dialog'} rodal-${animation}-${props.animationType}`;

  const CloseButton = props.showCloseButton ? (
    <span
      className="rodal-close"
      onClick={props.onClose}
      onKeyPress={event => {
        if (props.onClose && event.which === 13) {
          props.onClose(event);
        }
      }}
      tabIndex={0}
    />
  ) : null;

  const { width, height, measure, duration, customStyles, id } = props;

  const style = {
    width: width + measure,
    height: height + measure,
    animationDuration: duration + 'ms',
    WebkitAnimationDuration: duration + 'ms'
  };

  const mergedStyles = { ...style, ...customStyles };

  return (
    <div style={mergedStyles} className={className} id={id}>
      {props.children}
    </div>
  );
};

class Rodal extends React.Component {
  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    measure: PropTypes.string,
    visible: PropTypes.bool,
    showMask: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    closeMaskOnClick: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    animation: PropTypes.string,
    enterAnimation: PropTypes.string,
    leaveAnimation: PropTypes.string,
    duration: PropTypes.number,
    className: PropTypes.string,
    customStyles: PropTypes.object,
    customMaskStyles: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onAnimationEnd: PropTypes.func,
    id:PropTypes.string,
    isDrag:PropTypes.bool
  };

  static defaultProps = {
    width: 400,
    height: 240,
    measure: 'px',
    visible: false,
    showMask: true,
    closeOnEsc: true,
    closeMaskOnClick: true,
    showCloseButton: true,
    animation: '',
    enterAnimation: '',
    leaveAnimation: '',
    duration: 300,
    className: '',
    customStyles: {},
    customMaskStyles: {},
    isDrag: false,  // default value set isDrag
    isDisabled:false
  };

  state = {
    isShow: false,
    animationType: 'leave',
    isDrag: false,
    isDisabled: false,
    position: { x: 0, y: 0 }
  };

  componentDidMount() {
    if (this.props.visible) {
      this.enter();
    }
    if (this.props.isDrag) {
      this.setState({ isDrag: true });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.enter();
    }

    if (!this.props.visible && prevProps.visible) {
      this.leave();
    }
    if(this.state.isDisabled!=this.props.isDisabled) {
      this.setState({ isDisabled: prevProps.isDisabled });
    }
  }

  enter() {
    this.setState({ isShow: true, animationType: 'enter' });
  }

  leave() {
    this.setState(IS_IE_9 ? { isShow: false,isDisabled: false } : { animationType: 'leave' });
  }

  onKeyUp = event => {
    if (!this.props.closeOnEsc || event.keyCode !== 27) {
      return;
    }

    this.props.onClose(event);
  };

  animationEnd = event => {
    const { animationType } = this.state;
    const { closeOnEsc, onAnimationEnd } = this.props;

    if (animationType === 'leave') {
      this.setState({ isShow: false, isDrag:false, isDisabled:false });
    } else if (closeOnEsc) {
      this.el.focus();
    }

    if (event.target === this.el && onAnimationEnd) {
      onAnimationEnd();
    }
  };

  onStop(event, data) {
    // Viewport (wrapper)
    const documentElement = document.documentElement
    const wrapperHeight = (window.innerHeight || documentElement.clientHeight)
    const wrapperWidth = (window.innerWidth || documentElement.clientWidth)
    const modalObj = document.getElementsByClassName('avnet-modal')[0].getBoundingClientRect();

    let widthCalculationPositive = ((wrapperWidth / 2) - (modalObj.width / 2))
    let widthCalculationNegative = ((modalObj.width / 2) - (wrapperWidth / 2))
    let heightCalculationPositive = ((wrapperHeight / 2) - (modalObj.height / 2))
    let heightCalculationNegative = ((modalObj.height / 2) - (wrapperHeight / 2))

    let positionData = {}

    if (data.x <= widthCalculationNegative && data.y <= heightCalculationNegative) {
      positionData = {
        x: widthCalculationNegative,
        y: heightCalculationNegative
      }
    } else if (data.x >= widthCalculationPositive && data.y <= heightCalculationNegative) {
      positionData = {
        x: widthCalculationPositive,
        y: heightCalculationNegative
      }
    } else if (data.x >= widthCalculationPositive && data.y >= heightCalculationPositive) {
      positionData = {
        x: widthCalculationPositive,
        y: heightCalculationPositive
      }
    } else if (data.x <= widthCalculationNegative && data.y >= heightCalculationPositive) {
      positionData = {
        x: widthCalculationNegative,
        y: heightCalculationPositive
      }
    } else if (data.x >= widthCalculationPositive) {
      positionData = {
        x: widthCalculationPositive,
        y: data.y
      }
    }
    else if (data.x <= widthCalculationNegative) {
      positionData = {
        x: widthCalculationNegative,
        y: data.y
      }
    } else if (data.y >= heightCalculationPositive) {
      positionData = {
        x: data.x,
        y: heightCalculationPositive
      }
    } else if (data.y <= heightCalculationNegative) {
      positionData = {
        x: data.x,
        y: heightCalculationNegative
      }
    } else {
      positionData = {
        x: data.x,
        y: data.y
      }
    }
    this.setState({ position: positionData })
  }

  render() {
    const {
      closeMaskOnClick,
      onClose,
      customMaskStyles,
      showMask,
      duration,
      className,
      children
    } = this.props;

    const { isShow, animationType, isDrag } = this.state;

    const Mask = showMask ? (
      <div
        className="rodal-mask"
        style={customMaskStyles}
        onClick={closeMaskOnClick ? onClose : void 0}
      />
    ) : null;

    const style = {
      display: isShow ? '' : 'none',
      animationDuration: duration + 'ms',
      WebkitAnimationDuration: duration + 'ms'
    };

    return isDrag ?
      (
        <div className='modal-draggable'>
          {Mask}
          <Draggable disabled={this.state.isDisabled} position={this.state.position}
            onStop={(event, data) => this.onStop(event, data)}>
            <div
              style={style}
              className={cx('rodal-drag', `rodal-fade-${animationType}`, className)}
              onAnimationEnd={this.animationEnd}
              tabIndex="-1"
              ref={el => {
                this.el = el;
              }}
              onKeyUp={this.onKeyUp}
            >

              <Dialog {...this.props} animationType={animationType}>
                {children}
              </Dialog>
            </div>
          </Draggable>
        </div>
      ) :
      (
        <div
          style={style}
          className={cx('rodal', `rodal-fade-${animationType}`, className)}
          onAnimationEnd={this.animationEnd}
          tabIndex="-1"
          ref={el => {
            this.el = el;
          }}
          onKeyUp={this.onKeyUp}
        >
          {Mask}
          <Dialog {...this.props} animationType={animationType}>
            {children}
          </Dialog>
        </div>
      );
  }
}

export default Rodal;
